import { useEffect, useState } from "react";
import IncludeSideBar from "../../../Components/Sidebar/IncludeSideBar";
import { getAllReports } from "../../../apis/generate_report";
import useDebounce from "../../../utils/useDebounce";
import "./AllReports.css";

const REPORT_MEDIA_BASE_URL = "https://dreamapp.b-cdn.net";

const REPORT_TYPE_OPTIONS = [
  {
    value: "",
    label: "All Report Types",
    helper: "Fetch reports across every supported report type.",
  },
  {
    value: "video",
    label: "Video",
    helper: "Reports for video content.",
  },
  {
    value: "post",
    label: "Post",
    helper: "Reports for standard picture posts.",
  },
  {
    value: "user",
    label: "User",
    helper: "Reports against a user account.",
  },
  {
    value: "profile",
    label: "Profile",
    helper: "Reports specific to a user's profile.",
  },
  {
    value: "post_comment",
    label: "Post Comment",
    helper: "Reports on video comments.",
  },
  {
    value: "post_comment_reply",
    label: "Post Comment Reply",
    helper: "Reports on replies to video comments.",
  },
  {
    value: "picture_post",
    label: "Picture Post",
    helper: "Reports for image-based posts.",
  },
  {
    value: "picture_post_commen",
    label: "Picture Post Comment",
    helper: "Reports on image post comments.",
  },
];

const DEFAULT_PAGINATION = {
  totalItems: 0,
  totalPages: 1,
  currentPage: 1,
  itemsPerPage: 10,
};

const formatLabel = (value) => {
  if (!value) {
    return "N/A";
  }

  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getReportTypeLabel = (reportType) => {
  const option = REPORT_TYPE_OPTIONS.find((item) => item.value === reportType);
  return option?.label || formatLabel(reportType);
};

const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${REPORT_MEDIA_BASE_URL}/${value.replace(/^\/+/, "")}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  return new Date(value).toLocaleString();
};

const getVisiblePages = (currentPage, totalPages) => {
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  startPage = Math.max(1, endPage - maxVisiblePages + 1);

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );
};

function AllReports() {
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [reportType, setReportType] = useState("");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [preview, setPreview] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const selectedReportType =
    REPORT_TYPE_OPTIONS.find((item) => item.value === reportType) ||
    REPORT_TYPE_OPTIONS[0];

  useEffect(() => {
    let isMounted = true;

    const fetchReports = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getAllReports({
          page: currentPage,
          limit,
          reportType,
          searchTerm: debouncedSearchTerm.trim(),
        });

        if (!isMounted) {
          return;
        }

        setReports(response?.data || []);
        setPagination({
          totalItems: response?.pagination?.totalItems || 0,
          totalPages: response?.pagination?.totalPages || 1,
          currentPage: response?.pagination?.currentPage || currentPage,
          itemsPerPage: response?.pagination?.itemsPerPage || limit,
        });
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        setReports([]);
        setPagination({
          ...DEFAULT_PAGINATION,
          currentPage: 1,
          itemsPerPage: limit,
        });
        setError(
          fetchError?.response?.data?.message ||
          "Failed to load unified reports."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, reportType, debouncedSearchTerm, reloadKey]);

  const visiblePages = getVisiblePages(currentPage, pagination.totalPages);
  const showingFrom =
    pagination.totalItems === 0
      ? 0
      : (pagination.currentPage - 1) * pagination.itemsPerPage + 1;
  const showingTo =
    pagination.totalItems === 0
      ? 0
      : Math.min(showingFrom + reports.length - 1, pagination.totalItems);

  const handleClearFilters = () => {
    setSearchTerm("");
    setReportType("");
    setLimit(10);
    setCurrentPage(1);
    setReloadKey((previousValue) => previousValue + 1);
  };

  const openPreview = (type, url, title) => {
    setPreview({ type, url, title });
  };

  return (
    <IncludeSideBar>
      <div className="all-reports-page">
        <section className="all-reports-hero">
          <div>
            <span className="all-reports-eyebrow">Generate Report</span>
            <h1>Unified Reports</h1>
            <p>
              Review reports across videos, posts, profiles, accounts, and
              comments from one admin screen.
            </p>
          </div>

          <div className="all-reports-hero-note">
            <strong>Reporter username search</strong>
            <span>
              The `search` query parameter filters by the reporter's username.
            </span>
          </div>
        </section>

        <section className="all-reports-stats">
          <article className="all-reports-stat-card">
            <span>Total Reports</span>
            <strong>{pagination.totalItems}</strong>
            <small>Across all matching records</small>
          </article>

          <article className="all-reports-stat-card">
            <span>Current Type</span>
            <strong>{selectedReportType.label}</strong>
            <small>{selectedReportType.helper}</small>
          </article>

          <article className="all-reports-stat-card">
            <span>Rows Loaded</span>
            <strong>{reports.length}</strong>
            <small>Showing the current API response</small>
          </article>

          <article className="all-reports-stat-card">
            <span>Pagination</span>
            <strong>
              Page {pagination.currentPage} / {pagination.totalPages}
            </strong>
            <small>{pagination.itemsPerPage} items per page</small>
          </article>
        </section>

        <section className="all-reports-filters-card">
          <div className="all-reports-filters-grid">
            <label className="all-reports-field">
              <span>Reporter Username</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search reporter username"
              />
            </label>

            <label className="all-reports-field">
              <span>Report Type</span>
              <select
                value={reportType}
                onChange={(event) => {
                  setReportType(event.target.value);
                  setCurrentPage(1);
                }}
              >
                {REPORT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="all-reports-field all-reports-field-compact">
              <span>Rows Per Page</span>
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 20, 50, 80].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </label>

            <div className="all-reports-actions">
              <button
                className="all-reports-primary-btn"
                type="button"
                onClick={() => setReloadKey((previousValue) => previousValue + 1)}
              >
                Refresh
              </button>

              <button
                className="all-reports-secondary-btn"
                type="button"
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          <p className="all-reports-filter-helper">
            {selectedReportType.helper}
          </p>
        </section>

        {error ? <div className="all-reports-error">{error}</div> : null}

        <section className="all-reports-table-card">
          <div className="all-reports-table-header">
            <div>
              <h2>Report Queue</h2>
              <p>
                Showing {showingFrom}-{showingTo} of {pagination.totalItems}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="all-reports-status">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="all-reports-empty-state">
              <h3>No reports found</h3>
              <p>Try a different reporter username or report type filter.</p>
            </div>
          ) : (
            <div className="all-reports-table-shell">
              <table className="all-reports-table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Reporter</th>
                    <th>Type</th>
                    <th>Target</th>
                    <th>Reason</th>
                    <th>Description</th>
                    <th>Evidence</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => {
                    const imageUrl = resolveMediaUrl(report.media_image);
                    const videoUrl = resolveMediaUrl(report.media_video);
                    const reporterName =
                      report.reporter?.nickname ||
                      report.reporter?.username ||
                      "Unknown Reporter";

                    return (
                      <tr key={report.id}>
                        <td>
                          <div className="report-id-stack">
                            <strong>{report.id}</strong>
                            <span>Original #{report.original_id}</span>
                          </div>
                        </td>

                        <td>
                          <div className="reporter-cell">
                            {report.reporter?.profile_pic ? (
                              <img
                                src={report.reporter.profile_pic}
                                alt={reporterName}
                                className="reporter-avatar"
                              />
                            ) : (
                              <div className="reporter-avatar-fallback">
                                {reporterName.charAt(0).toUpperCase()}
                              </div>
                            )}

                            <div className="reporter-meta">
                              <strong>{reporterName}</strong>
                              <span>@{report.reporter?.username || "unknown"}</span>
                              <small>ID #{report.reporter_id}</small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="report-type-pill">
                            {getReportTypeLabel(report.report_type)}
                          </span>
                        </td>

                        <td>
                          <div className="report-id-stack">
                            <strong>Target #{report.target_id ?? "N/A"}</strong>
                            <span>Reporter #{report.reporter_id ?? "N/A"}</span>
                          </div>
                        </td>

                        <td>
                          <span className="report-reason-pill">
                            {formatLabel(report.reason)}
                          </span>
                        </td>

                        <td>
                          <p className="report-description">
                            {report.description || "No description provided."}
                          </p>
                        </td>

                        <td>
                          <div className="report-media-actions">
                            {imageUrl ? (
                              <button
                                type="button"
                                className="report-media-btn"
                                onClick={() =>
                                  openPreview("image", imageUrl, `${report.id} image`)
                                }
                              >
                                View Image
                              </button>
                            ) : null}

                            {videoUrl ? (
                              <button
                                type="button"
                                className="report-media-btn"
                                onClick={() =>
                                  openPreview("video", videoUrl, `${report.id} video`)
                                }
                              >
                                View Video
                              </button>
                            ) : null}

                            {!imageUrl && !videoUrl ? (
                              <span className="report-no-media">No media</span>
                            ) : null}
                          </div>
                        </td>

                        <td>{formatDateTime(report.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination.totalPages > 1 ? (
            <div className="all-reports-pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || loading}
              >
                First
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={page === currentPage ? "active" : ""}
                  onClick={() => setCurrentPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(page + 1, pagination.totalPages)
                  )
                }
                disabled={currentPage === pagination.totalPages || loading}
              >
                Next
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages || loading}
              >
                Last
              </button>
            </div>
          ) : null}
        </section>

        {preview ? (
          <div
            className="all-reports-preview-backdrop"
            onClick={() => setPreview(null)}
          >
            <div
              className="all-reports-preview-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="all-reports-preview-close"
                type="button"
                onClick={() => setPreview(null)}
              >
                Close
              </button>

              <div className="all-reports-preview-header">
                <h3>{preview.title}</h3>
                <a href={preview.url} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              </div>

              {preview.type === "image" ? (
                <img
                  src={preview.url}
                  alt={preview.title}
                  className="all-reports-preview-image"
                />
              ) : (
                <video
                  src={preview.url}
                  className="all-reports-preview-video"
                  controls
                  autoPlay
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </IncludeSideBar>
  );
}

export default AllReports;
